import { doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebase';
import Logger from './logger';
import { NOTIFICATION_TYPES } from '../constants/notificationTypes';

export interface NotificationState {
  pageVisits: {
    myLoads: Date | null;
    availableLoads: Date | null;
    partners: Date | null;
    payments: Date | null;
    // Shipper-specific pages
    purchaseOrders: Date | null;
    shippingSchedule: Date | null;
    driverUpdates: Date | null;
    carrierPartners: Date | null;
    dashboard: Date | null;
  };
  loadVisits: {
    [loadId: string]: Date | null;
  };
  lastNotificationCheck: Date | null;
}

export interface Notification {
  id: string;
  type: string;
  read: boolean;
  createdAt: Date;
  loadId?: string;
  poNumber?: string;
  [key: string]: any;
}

// Get notification state for current user
export const getNotificationState = async (userId: string): Promise<NotificationState> => {
  try {
    const stateDoc = await getDoc(doc(db, 'notificationStates', userId));
    
    if (stateDoc.exists()) {
      const data = stateDoc.data();
      return {
        pageVisits: {
          myLoads: data.pageVisits?.myLoads?.toDate() || null,
          availableLoads: data.pageVisits?.availableLoads?.toDate() || null,
          partners: data.pageVisits?.partners?.toDate() || null,
          payments: data.pageVisits?.payments?.toDate() || null,
          // Shipper-specific pages
          purchaseOrders: data.pageVisits?.purchaseOrders?.toDate() || null,
          shippingSchedule: data.pageVisits?.shippingSchedule?.toDate() || null,
          driverUpdates: data.pageVisits?.driverUpdates?.toDate() || null,
          carrierPartners: data.pageVisits?.carrierPartners?.toDate() || null,
          dashboard: data.pageVisits?.dashboard?.toDate() || null,
        },
        loadVisits: data.loadVisits || {},
        lastNotificationCheck: data.lastNotificationCheck?.toDate() || null,
      };
    }
    
    // For new users, initialize all page visits to 30 days ago
    // This allows notifications from the last 30 days to show, but not older ones
    // This prevents showing all historical unread notifications on first visit
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const now = new Date();
    
    const initialState: NotificationState = {
      pageVisits: {
        myLoads: thirtyDaysAgo,
        availableLoads: thirtyDaysAgo,
        partners: thirtyDaysAgo,
        payments: thirtyDaysAgo,
        // Shipper-specific pages
        purchaseOrders: thirtyDaysAgo,
        shippingSchedule: thirtyDaysAgo,
        driverUpdates: thirtyDaysAgo,
        carrierPartners: thirtyDaysAgo,
        dashboard: thirtyDaysAgo,
      },
      loadVisits: {},
      lastNotificationCheck: now,
    };
    
    // Don't persist initial state to Firestore yet - wait until user visits a page
    // This avoids unnecessary writes and timestamp conversion issues
    // The state will be saved when updatePageVisit is called for the first time
    
    return initialState;
  } catch (error) {
    Logger.error('Error getting notification state:', error as Error);
    // On error, initialize to 30 days ago to prevent showing all old notifications
    // but still allow recent notifications to show
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const now = new Date();
    return {
      pageVisits: {
        myLoads: thirtyDaysAgo,
        availableLoads: thirtyDaysAgo,
        partners: thirtyDaysAgo,
        payments: thirtyDaysAgo,
        // Shipper-specific pages
        purchaseOrders: thirtyDaysAgo,
        shippingSchedule: thirtyDaysAgo,
        driverUpdates: thirtyDaysAgo,
        carrierPartners: thirtyDaysAgo,
        dashboard: thirtyDaysAgo,
      },
      loadVisits: {},
      lastNotificationCheck: now,
    };
  }
};

// Update page visit timestamp
export const updatePageVisit = async (userId: string, pageType: keyof NotificationState['pageVisits']): Promise<void> => {
  try {
    const now = new Date();
    const stateRef = doc(db, 'notificationStates', userId);
    
    await setDoc(stateRef, {
      [`pageVisits.${pageType}`]: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    
    Logger.debug(`Updated ${pageType} visit time for user ${userId}`);
  } catch (error) {
    Logger.error(`Error updating ${pageType} visit:`, error as Error);
  }
};

// Update load visit timestamp
export const updateLoadVisit = async (userId: string, loadId: string): Promise<void> => {
  try {
    const now = new Date();
    const stateRef = doc(db, 'notificationStates', userId);
    
    await setDoc(stateRef, {
      [`loadVisits.${loadId}`]: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    
    Logger.debug(`Updated load ${loadId} visit time for user ${userId}`);
  } catch (error) {
    Logger.error(`Error updating load ${loadId} visit:`, error as Error);
  }
};

// Check if notification is newer than last page visit
export const isNewerThanPageVisit = (notification: Notification, lastVisit: Date | null): boolean => {
  // If no last visit timestamp, don't show old notifications (older than 30 days)
  // This prevents showing all historical unread notifications on first visit
  if (!lastVisit) {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    // Only show notifications from the last 30 days if no visit history exists
    return notification.createdAt > thirtyDaysAgo;
  }
  return notification.createdAt > lastVisit;
};

// Check if notification is newer than last load visit
export const isNewerThanLoadVisit = (notification: Notification, lastVisit: Date | null): boolean => {
  // If no last visit timestamp, don't show old notifications (older than 30 days)
  // This prevents showing all historical unread notifications on first visit
  if (!lastVisit) {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    // Only show notifications from the last 30 days if no visit history exists
    return notification.createdAt > thirtyDaysAgo;
  }
  return notification.createdAt > lastVisit;
};

// Get notification category for page mapping
export const getNotificationCategory = (type: string): string | null => {
  const NOTIFICATION_CATEGORIES = {
    myLoads: [
      'load_request',
      'load_status_update', 
      'load_modified',
      NOTIFICATION_TYPES.LOAD_CANCELLED,
      NOTIFICATION_TYPES.CANCELLATION_REQUEST,
      'load_tracking_update',
      NOTIFICATION_TYPES.LOAD_STARTED,
      'load_completed',
      'carrier_assigned',
      'carrier_accepted',
      'carrier_accept_broker',
      'unified_partner_request'
    ],
    availableLoads: [
      'make_offer',
      'carrier_counter_offer',
      'shipper_accept_counter',
      'shipper_reject_counter'
    ],
    partners: [
      'partner_request'
    ],
    payments: [
      'payment_received',
      'invoice_generated',
      'invoice_received',
      'factoring_requested',
      'payment_status_updated'
    ],
    purchaseOrders: [
      'carrier_accept',
      'carrier_accept_broker',
      NOTIFICATION_TYPES.LOAD_STARTED,
      'load_completed',
      'load_status_update',
      'load_modified',
      NOTIFICATION_TYPES.LOAD_CANCELLED,
      NOTIFICATION_TYPES.CANCELLATION_APPROVED,
      NOTIFICATION_TYPES.CANCELLATION_REJECTED,
      'unified_partner_request'
    ],
    driverUpdates: [
      'load_tracking_update',
      'load_status_update',
      NOTIFICATION_TYPES.LOAD_STARTED,
      NOTIFICATION_TYPES.LOAD_OFF_ROUTE
    ]
  };

  for (const [category, types] of Object.entries(NOTIFICATION_CATEGORIES)) {
    if (types.includes(type)) {
      return category;
    }
  }
  
  return null;
};

// Check if notification is load-specific
export const isLoadSpecificNotification = (notification: Notification): boolean => {
  return !!(notification.loadId || notification.poNumber);
};

// Get load ID from notification
export const getLoadIdFromNotification = (notification: Notification): string | null => {
  return notification.loadId || notification.poNumber || null;
};

// ===== NOTIFICATION READ MARKING FUNCTIONS =====

// Mark notifications as read for a specific page type
export const markNotificationsAsRead = async (
  userId: string, 
  pageType: keyof NotificationState['pageVisits']
): Promise<void> => {
  try {
    // Get notification types that should be marked as read for this page
    const notificationTypes = getNotificationTypesForPage(pageType);
    
    if (notificationTypes.length === 0) {
      Logger.debug(`No notification types to mark as read for page: ${pageType}`);
      return;
    }

    // Query for unread notifications of these types for this user
    // Notifications can use different fields: recipientId, shipperId, brokerId, carrierId, or userId
    // We need to check all possible fields since Firestore doesn't support OR queries easily
    const queries = [
      // Query by recipientId (carrier notifications)
      query(
        collection(db, 'notifications'),
        where('recipientId', '==', userId),
        where('read', '==', false),
        where('type', 'in', notificationTypes)
      ),
      // Query by shipperId (shipper notifications)
      query(
        collection(db, 'notifications'),
        where('shipperId', '==', userId),
        where('read', '==', false),
        where('type', 'in', notificationTypes)
      ),
      // Query by brokerId (broker notifications)
      query(
        collection(db, 'notifications'),
        where('brokerId', '==', userId),
        where('read', '==', false),
        where('type', 'in', notificationTypes)
      ),
      // Query by carrierId (carrier notifications)
      query(
        collection(db, 'notifications'),
        where('carrierId', '==', userId),
        where('read', '==', false),
        where('type', 'in', notificationTypes)
      ),
      // Query by userId (fallback for any user type)
      query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false),
        where('type', 'in', notificationTypes)
      )
    ];

    // Execute all queries and merge results
    const allSnapshots = await Promise.all(
      queries.map(q => getDocs(q).catch(err => {
        // Handle missing index errors gracefully - return empty snapshot
        if (err instanceof Error && 'code' in err && err.code === 'failed-precondition' && err.message.includes('index')) {
          Logger.warn(`Index not ready for notification marking query (non-critical):`, { message: err.message });
          return { docs: [], empty: true } as any;
        }
        throw err;
      }))
    );

    // Collect all notification document refs, deduplicating by document ID
    const notificationRefs = new Map<string, any>();
    allSnapshots.forEach(snapshot => {
      snapshot.forEach((doc: any) => {
        if (!notificationRefs.has(doc.id)) {
          notificationRefs.set(doc.id, doc.ref);
        }
      });
    });

    if (notificationRefs.size === 0) {
      Logger.debug(`No unread notifications found for user ${userId} on page ${pageType}`);
      return;
    }

    // Use batch update for efficiency
    const batch = writeBatch(db);
    notificationRefs.forEach((notificationRef) => {
      batch.update(notificationRef, {
        read: true,
        readAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();
    Logger.debug(`Marked ${notificationRefs.size} notifications as read for user ${userId} on page ${pageType}`);
    
  } catch (error) {
    // Handle missing index error gracefully
    if (error instanceof Error && 'code' in error && error.code === 'failed-precondition' && error.message.includes('index')) {
      Logger.warn(`Index not ready for notification marking query for user ${userId} on page ${pageType}. Skipping marking.`);
      Logger.warn('Create the required indexes for: recipientId, shipperId, brokerId, carrierId, userId (each with read and type)');
      return; // Don't throw, just skip marking
    }
    Logger.error(`Error marking notifications as read for page ${pageType}:`, error as Error);
    // Don't throw error to prevent breaking the app
  }
};

// Mark specific load notifications as read
export const markLoadNotificationsAsRead = async (
  userId: string, 
  loadId: string
): Promise<void> => {
  try {
    // Query for unread notifications related to this specific load
    // Notifications can use different fields: recipientId, shipperId, brokerId, carrierId, or userId
    // We need to check all possible fields since Firestore doesn't support OR queries easily
    const queries = [
      // Query by recipientId (carrier notifications)
      query(
        collection(db, 'notifications'),
        where('recipientId', '==', userId),
        where('read', '==', false),
        where('loadId', '==', loadId)
      ),
      // Query by shipperId (shipper notifications)
      query(
        collection(db, 'notifications'),
        where('shipperId', '==', userId),
        where('read', '==', false),
        where('loadId', '==', loadId)
      ),
      // Query by brokerId (broker notifications)
      query(
        collection(db, 'notifications'),
        where('brokerId', '==', userId),
        where('read', '==', false),
        where('loadId', '==', loadId)
      ),
      // Query by carrierId (carrier notifications)
      query(
        collection(db, 'notifications'),
        where('carrierId', '==', userId),
        where('read', '==', false),
        where('loadId', '==', loadId)
      ),
      // Query by userId (fallback for any user type)
      query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false),
        where('loadId', '==', loadId)
      )
    ];

    // Execute all queries and merge results
    const allSnapshots = await Promise.all(
      queries.map(q => getDocs(q).catch(err => {
        // Handle missing index errors gracefully - return empty snapshot
        if (err instanceof Error && 'code' in err && err.code === 'failed-precondition' && err.message.includes('index')) {
          Logger.warn(`Index not ready for load notification marking query (non-critical):`, { message: err.message });
          return { docs: [], empty: true } as any;
        }
        throw err;
      }))
    );

    // Collect all notification document refs, deduplicating by document ID
    const notificationRefs = new Map<string, any>();
    allSnapshots.forEach(snapshot => {
      snapshot.forEach((doc: any) => {
        if (!notificationRefs.has(doc.id)) {
          notificationRefs.set(doc.id, doc.ref);
        }
      });
    });

    if (notificationRefs.size === 0) {
      Logger.debug(`No unread load notifications found for user ${userId} and load ${loadId}`);
      return;
    }

    // Use batch update for efficiency
    const batch = writeBatch(db);
    notificationRefs.forEach((notificationRef) => {
      batch.update(notificationRef, {
        read: true,
        readAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();
    Logger.debug(`Marked ${notificationRefs.size} load notifications as read for user ${userId} and load ${loadId}`);
    
  } catch (error) {
    // Handle missing index error gracefully
    if (error instanceof Error && 'code' in error && error.code === 'failed-precondition' && error.message.includes('index')) {
      Logger.warn(`Index not ready for load notification marking query for user ${userId} and load ${loadId}. Skipping marking.`);
      Logger.warn('Create the required indexes for: recipientId, shipperId, brokerId, carrierId, userId (each with read and loadId)');
      return; // Don't throw, just skip marking
    }
    Logger.error(`Error marking load notifications as read for load ${loadId}:`, error as Error);
    // Don't throw error to prevent breaking the app
  }
};

// Get notification types that should be marked as read for a specific page
const getNotificationTypesForPage = (pageType: keyof NotificationState['pageVisits']): string[] => {
  const pageNotificationMap: Record<string, string[]> = {
    myLoads: [
      'load_request',
      'load_status_update',
      'load_modified',
      NOTIFICATION_TYPES.LOAD_CANCELLED,
      NOTIFICATION_TYPES.CANCELLATION_REQUEST,
      'load_tracking_update',
      NOTIFICATION_TYPES.LOAD_STARTED,
      'load_completed',
      'carrier_assigned',
      'carrier_accepted',
      'carrier_accept_broker',
      'unified_partner_request'
    ],
    availableLoads: [
      'make_offer',
      'carrier_counter_offer',
      'shipper_accept_counter',
      'shipper_reject_counter'
    ],
    partners: [
      'partner_request'
    ],
    carrierPartners: [
      'partner_request',
      'make_offer',
      'carrier_counter_offer'
    ],
    payments: [
      'payment_received',
      'invoice_generated',
      'invoice_received',
      'factoring_requested',
      'payment_status_updated'
    ],
    purchaseOrders: [
      'carrier_decline',
      'carrier_accept',
      'carrier_accept_broker',
      NOTIFICATION_TYPES.LOAD_STARTED,
      'load_completed',
      'load_status_update',
      'load_modified',
      NOTIFICATION_TYPES.LOAD_CANCELLED,
      NOTIFICATION_TYPES.CANCELLATION_APPROVED,
      NOTIFICATION_TYPES.CANCELLATION_REJECTED,
      NOTIFICATION_TYPES.TONU_INVOICE_CREATED,
      'unified_partner_request'
    ],
    shippingSchedule: [
      'carrier_assigned',
      'load_tracking_update',
      'load_request'
    ],
    driverUpdates: [
      'load_tracking_update',
      'load_status_update',
      NOTIFICATION_TYPES.LOAD_STARTED,
      NOTIFICATION_TYPES.LOAD_OFF_ROUTE
    ],
    dashboard: [] // Dashboard doesn't have specific notification types
  };

  return pageNotificationMap[pageType] || [];
};

// Clean up old read notifications (older than 30 days)
export const cleanupReadNotifications = async (userId: string): Promise<void> => {
  try {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    // Query for read notifications older than 30 days
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('recipientId', '==', userId),
      where('read', '==', true),
      where('readAt', '<', thirtyDaysAgo)
    );

    const snapshot = await getDocs(notificationsQuery);
    
    if (snapshot.empty) {
      Logger.debug(`No old read notifications found for user ${userId}`);
      return;
    }

    // Use batch delete for efficiency
    const batch = writeBatch(db);

    snapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    Logger.debug(`Cleaned up ${snapshot.docs.length} old read notifications for user ${userId}`);
    
  } catch (error) {
    // Handle missing index error gracefully
    if (error instanceof Error && 'code' in error && error.code === 'failed-precondition' && error.message.includes('index')) {
      Logger.warn(`Index not ready for cleanup query for user ${userId}. Skipping cleanup.`);
      Logger.warn('Create the required index: recipientId (Ascending), read (Ascending), readAt (Ascending)');
      return; // Don't throw, just skip cleanup
    }
    Logger.error(`Error cleaning up read notifications for user ${userId}:`, error as Error);
    // Don't throw error to prevent breaking the app
  }
};
